// tabs/CounseleesTab.tsx
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Avatar, Badge, Button, Card, Center, Group, Loader, ScrollArea,
  SimpleGrid, Stack, Text, ThemeIcon, ActionIcon, Tooltip, Drawer, Select, Divider,
} from '@mantine/core';
import { IconSearch, IconUserPlus, IconUsers, IconPhone, IconMail, IconTrash, IconRefresh, IconFileImport, IconWand } from '@tabler/icons-react';
import { TextInput, MemberDetailModal } from '@/components';
import ImportFromRequestModal from './ImportFromRequestModal';
import MergeFromSubmissionModal from '@/components/common/MergeFromSubmissionModal';
import {
  useCareCaseService, useMemberService, useMemberTypeService, useCareRequestService,
} from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';

interface CounseleesTabProps {
  caseID: number;
  active: boolean;
  onCaseChange?: () => void;
  /**
   * The request this case was created from, when it was.
   *
   * ⚠️ Drives whether the "Import from request" action exists at all. A hand-created case has no
   * request behind it, and offering an import that can only ever be empty is worse than not offering
   * one.
   */
  requestID?: number | null;
}

// Shape from searchMembers response data[]
interface Member {
  memberID: number;
  userId: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  dateOfBirth: string | null;
  gender: string | null;
  maritalStatusID: number;
  email: string;
  cellNumber: string | null;
  altContact: string | null;
  telephone: string | null;
  isMember: boolean;
  isActive: boolean;
  /** Submitted the request, as opposed to a counselee who only needs care (SDK 0.0.58). */
  isRequester?: boolean;
  avatar: string | null;
  typeID: number;
  memberCode: string | null;
  modifiedDateTime: number;
  createdDateTime: number | null;
  // joined locally after enrichment
  isMain?: boolean;
  hasConsent?: boolean;
}

interface MemberType {
  typeID: number;
  name: string;
}

const fullName = (m: Member) =>
  [m.firstName, m.lastName].filter(Boolean).join(' ').trim() || `Member ${m.memberID}`;

const getInitials = (m: Member) =>
  [m.firstName?.[0], m.lastName?.[0]].filter(Boolean).join('').toUpperCase() || 'M';

// ── Counselee grid card ──────────────────────────────────────────────────────
function CounseleeCard({
  member,
  onRemove,
  removing,
  onOpenDetails,
  onFillFromRequest,
}: {
  member: Member;
  onRemove: () => void;
  removing: boolean;
  onOpenDetails: () => void;
  /** Present only when the request captured something this member record is missing. */
  onFillFromRequest?: () => void;
}) {
  return (
    <Card
      withBorder
      radius="md"
      padding="lg"
      h="100%"
      onClick={onOpenDetails}
      style={{ cursor: 'pointer' }}
    >
      <Group justify="center" mb="sm">
        <Avatar src={member.avatar ?? undefined} alt={fullName(member)} size="xl" radius="md">
          {getInitials(member)}
        </Avatar>
      </Group>

      <Text fw={700} ta="center" size="md" mb={4} lineClamp={1}>
        {fullName(member)}
      </Text>

      {/* Who this person is on the case.
          ⚠️ `isRequester` and `isMain` are independent: a self-registration is both, an on-behalf
          requester is neither a counselee nor the main one. Badging them separately is the only way
          to tell "submitted the request" from "needs care". */}
      <Group justify="center" gap={6} mb={4}>
        {member.isRequester && (
          <Badge size="sm" variant="light" color="grape">Requester</Badge>
        )}
        {member.isMain && (
          <Badge size="sm" variant="light" color="blue">
            {member.isRequester ? 'Needs care' : 'Main Participant'}
          </Badge>
        )}
        {!member.isMain && !member.isRequester && (
          <Badge size="sm" variant="light" color="teal">Needs care</Badge>
        )}
        {/* <Badge size="sm" variant="light" color={member.hasConsent ? 'green' : 'red'}>
          {member.hasConsent ? 'Consent Given' : 'No Consent'}
        </Badge> */}
      </Group>

      <Stack align="center" gap={2}>
        {member.email
          ? <Text size="sm" c="dimmed">Email: {member.email}</Text>
          : <Text size="sm" c="dimmed">No email on record</Text>
        }
        {(member.cellNumber || member.telephone || member.altContact)
          ? <Text size="sm" c="dimmed">Phone: {member.cellNumber ?? member.telephone ?? member.altContact}</Text>
          : <Text size="sm" c="dimmed">No phone on record</Text>
        }
      </Stack>

      <Group justify="flex-end" gap={6} mt="md">
        {/* Only rendered when there is genuinely something to apply — see `submittedFor`. */}
        {onFillFromRequest && (
          <Tooltip label="Fill missing details from the request" withArrow>
            <ActionIcon
              variant="light"
              color="teal"
              onClick={(e) => { e.stopPropagation(); onFillFromRequest(); }}
            >
              <IconWand size={16} />
            </ActionIcon>
          </Tooltip>
        )}
        <Tooltip label="Remove from case" withArrow>
          <ActionIcon
            variant="light"
            color="red"
            loading={removing}
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Card>
  );
}

// ── Search result card (used inside drawer) ────────────────────────────────
function SearchResultCard({
  member,
  alreadyAdded,
  addingId,
  onAdd,
}: {
  member: Member;
  alreadyAdded: boolean;
  addingId: number | null;
  onAdd: (isMain: boolean) => void;
}) {
  return (
    <Card withBorder radius="md" padding="md">
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Group gap="md" wrap="nowrap" style={{ overflow: 'hidden', flex: 1 }}>
          <Avatar src={member.avatar ?? undefined} radius="xl" size={44} color="teal">
            {getInitials(member)}
          </Avatar>
          <Stack gap={2} style={{ overflow: 'hidden', flex: 1 }}>
            <Text fw={600} size="sm" truncate>{fullName(member)}</Text>
            {member.email && (
              <Group gap={6} wrap="nowrap">
                <IconMail size={13} style={{ flexShrink: 0, color: 'var(--mantine-color-dimmed)' }} />
                <Text size="xs" c="dimmed" truncate>{member.email}</Text>
              </Group>
            )}
            {(member.cellNumber || member.telephone) && (
              <Group gap={6} wrap="nowrap">
                <IconPhone size={13} style={{ flexShrink: 0, color: 'var(--mantine-color-dimmed)' }} />
                <Text size="xs" c="dimmed">{member.cellNumber ?? member.telephone}</Text>
              </Group>
            )}
          </Stack>
        </Group>

        {alreadyAdded ? (
          <Badge color="gray" variant="light" style={{ flexShrink: 0 }}>Already added</Badge>
        ) : (
          <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
            <Button
              size="xs"
              variant="light"
              loading={addingId === member.memberID}
              leftSection={<IconUserPlus size={14} />}
              onClick={() => onAdd(false)}
            >
              Add
            </Button>
            <Button
              size="xs"
              loading={addingId === member.memberID}
              onClick={() => onAdd(true)}
            >
              Add as Main
            </Button>
          </Group>
        )}
      </Group>
    </Card>
  );
}

function CounseleeCardSkeleton() {
  return (
    <Card withBorder radius="md" padding="md" h="100%">
      <Stack gap="sm">
        <Avatar radius="xl" size={52} color="gray">M</Avatar>
        <Stack gap={4}>
          <Text size="sm" c="dimmed">Loading…</Text>
          <Text size="xs" c="dimmed">—</Text>
          <Text size="xs" c="dimmed">—</Text>
        </Stack>
      </Stack>
    </Card>
  );
}

// ── Add Counselees Drawer ──────────────────────────────────────────────────
function AddCounseleesDrawer({
  opened,
  onClose,
  caseID,
  alreadyAdded,
  onAdded,
}: {
  opened: boolean;
  onClose: () => void;
  caseID: number;
  alreadyAdded: (m: Member) => boolean;
  onAdded: () => Promise<void> | void;
}) {
  const careCaseService  = useCareCaseService();
  const memberService    = useMemberService();
  const memberTypeService = useMemberTypeService();

  const [search, setSearch]       = useState('');
  const [results, setResults]     = useState<Member[]>([]);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId]   = useState<number | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [allMemberTypes, setAllMemberTypes] = useState<MemberType[]>([]);
  const [creatingMember, setCreatingMember] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [email, setEmail]         = useState('');
  const [cellNumber, setCellNumber] = useState('');
  const [typeID, setTypeID]       = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    memberTypeService.getAll()
      .then((res: any) => setAllMemberTypes(res?.data ?? res ?? []))
      .catch(console.error);
  }, [opened, memberTypeService]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!search.trim()) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res: any = await memberService.searchMembers(search.trim());
        const list: Member[] = res?.data ?? res ?? [];
        setResults(list);
      } catch (e) {
        console.error('Error searching members:', e);
        toast.error('Error', 'Could not search members.');
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search, memberService]);

  const resetCreateForm = () => {
    setFirstName(''); setLastName(''); setEmail(''); setCellNumber(''); setTypeID(null);
    setShowCreate(false);
  };

  const resetAll = () => {
    setSearch(''); setResults([]);
    resetCreateForm();
  };

  const addToCase = async (member: Member, isMain: boolean) => {
    setAddingId(member.memberID);
    try {
      await careCaseService.addMember(caseID, member.memberID, isMain);
      await onAdded();
      toast.success('Counselee Added', `${fullName(member)} has been added to the case.`);
      setSearch('');
      setResults([]);
    } catch (e) {
      console.error('Error adding member:', e);
      toast.error('Error', 'Could not add counselee. Please try again.');
    } finally {
      setAddingId(null);
    }
  };

  // ── Create a brand-new member when search comes up empty, then add to case ──
  const createAndAddMember = async () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !typeID) return;
    setCreatingMember(true);
    try {
      const newMember: Partial<Member> = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        cellNumber: cellNumber.trim() || null,
        typeID: Number(typeID),
        isMember: false,
        hasConsent: true,
        isActive: true,
      };

      const res: any = await memberService.create(newMember as Member);
      const created: Member = res?.data ?? res;

      await careCaseService.addMember(caseID, created.memberID, false);
      await onAdded();

      toast.success('Member Created', `${fullName(created)} was created and added to the case.`);
      resetCreateForm();
      setSearch('');
      setResults([]);
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to create member.');
    } finally {
      setCreatingMember(false);
    }
  };

  const noResults = !searching && search.trim().length > 0 && results.length === 0;

  return (
    <Drawer
      opened={opened}
      onClose={() => { onClose(); resetAll(); }}
      title={
        <Group gap="xs">
          <IconUserPlus size={18} />
          <Text fw={600}>Add Counselees</Text>
        </Group>
      }
      position="right"
      size={620}
      padding="md"
    >
      <Stack gap="md">
        <TextInput
          placeholder="Search members by name…"
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />

        {searching && <Center py="sm"><Loader size="sm" /></Center>}

        {!searching && results.length > 0 && (
          <ScrollArea.Autosize mah={390}>
            <Stack gap="xs">
              {results.map((m) => (
                <SearchResultCard
                  key={m.memberID}
                  member={m}
                  alreadyAdded={alreadyAdded(m)}
                  addingId={addingId}
                  onAdd={(isMain) => addToCase(m, isMain)}
                />
              ))}
            </Stack>
          </ScrollArea.Autosize>
        )}

        {noResults && !showCreate && (
          <Card withBorder radius="md" padding="sm">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">No members found for "{search}".</Text>
              <Button size="xs" variant="light" onClick={() => setShowCreate(true)}>
                Create New Member
              </Button>
            </Group>
          </Card>
        )}

        {showCreate && (
          <Card withBorder radius="md" padding="sm">
            <Stack gap="sm">
              <Text size="sm" fw={600}>New Member</Text>
              <Group grow>
                <TextInput
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.currentTarget.value)}
                />
                <TextInput
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.currentTarget.value)}
                />
              </Group>
              <TextInput
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
              />
              <TextInput
                placeholder="Cell number"
                value={cellNumber}
                onChange={(e) => setCellNumber(e.currentTarget.value)}
              />
              <Select
                placeholder="Member type"
                data={allMemberTypes.map((t) => ({ value: String(t.typeID), label: t.name }))}
                value={typeID}
                onChange={setTypeID}
                searchable
                clearable
              />
              <Group justify="flex-end" gap="xs">
                <Button variant="subtle" size="xs" onClick={resetCreateForm}>
                  Cancel
                </Button>
                <Button
                  size="xs"
                  loading={creatingMember}
                  disabled={!firstName.trim() || !lastName.trim() || !email.trim() || !typeID}
                  onClick={createAndAddMember}
                >
                  Create & Add to Case
                </Button>
              </Group>
            </Stack>
          </Card>
        )}

        {!search.trim() && (
          <>
            <Divider />
            <Text size="sm" c="dimmed" ta="center">
              Search for a member above, or create a new one if they don't exist yet.
            </Text>
          </>
        )}
      </Stack>
    </Drawer>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function CounseleesTab({ caseID, active, onCaseChange, requestID }: CounseleesTabProps) {
  const careCaseService = useCareCaseService();
  const requestService = useCareRequestService();

  /**
   * Member ids already on the case.
   *
   * ⚠️ Just the ids — used only to grey out people already added and to tell the import which people
   * to skip. Holding the whole `CaseMember` meant carrying fields nothing reads.
   */
  const [caseMemberIDs, setCaseMemberIDs] = useState<number[]>([]);
  const [counselees, setCounselees]   = useState<Member[]>([]);
  const [loadingCounselees, setLoadingCounselees] = useState(false);
  /** Card click opens the read-only detail modal. */
  const [detailMember, setDetailMember] = useState<Member | null>(null);
  const [removingId, setRemovingId]     = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen]     = useState(false);
  const [importOpen, setImportOpen]     = useState(false);
  /** The counselee whose request details are being reviewed, or null. */
  const [mergeMember, setMergeMember]   = useState<Member | null>(null);
  /**
   * What the request captured, keyed by memberID.
   *
   * ⚠️ Loaded once per case, not per counselee. `getView().members` reads from the member cache, so it
   * cannot answer "what did this person actually give on the form?" — only the request can. Empty when
   * the case was created by hand, which disables the action.
   */
  const [submittedByMemberID, setSubmittedByMemberID] = useState<Record<number, {
    dateOfBirth?: string | null; gender?: string | null; email?: string | null; phone?: string | null;
  }>>({});

  /**
   * One call, not 1 + N.
   *
   * ⚠️ This used to call `members()` and then `memberService.searchMembers` **per counselee** to fill in
   * names and contacts. `getView(caseID).members` returns `CareCaseMemberView[]` with all of it already
   * — plus `isRequester` and `dateOfBirth`, which the bare `CareCaseMember` entity does not carry at
   * all. Do not go back to the entity endpoint for this list.
   */
  const fetchCounselees = useCallback(async () => {
    if (!caseID) return;
    setLoadingCounselees(true);
    try {
      const view = await careCaseService.getView(caseID);
      const rows = view?.members ?? [];

      setCaseMemberIDs(rows.map((m) => m.memberID));

      setCounselees(rows.map((m) => {
        // ⚠️ The view gives one `fullName`; the card wants first/last for its initials. Splitting on
        // the last space is a display convenience only — nothing is written back from these.
        const full = (m.fullName ?? '').trim();
        const cut = full.lastIndexOf(' ');
        return {
          memberID: m.memberID,
          userId: '',
          firstName: cut > 0 ? full.slice(0, cut) : full || 'Member',
          middleName: null,
          lastName: cut > 0 ? full.slice(cut + 1) : '',
          dateOfBirth: m.dateOfBirth ?? null,
          gender: null,
          maritalStatusID: 0,
          email: m.email ?? '',
          cellNumber: m.phone ?? null,
          altContact: null,
          telephone: null,
          isMember: true,
          isActive: true,
          isRequester: m.isRequester,
          avatar: m.avatar ?? null,
          typeID: 0,
          memberCode: null,
          modifiedDateTime: 0,
          createdDateTime: null,
          isMain: m.isMain,
          hasConsent: m.hasConsent,
        } satisfies Member;
      }));
    } catch (e) {
      console.error('Error fetching counselees:', e);
      toast.error('Error', 'Could not load counselees.');
    } finally {
      setLoadingCounselees(false);
    }
  }, [caseID, careCaseService]);

  useEffect(() => { if (active) fetchCounselees(); }, [active, fetchCounselees]);

  useEffect(() => {
    if (!active || requestID == null) return;
    let cancelled = false;
    requestService.getById(requestID)
      .then((request) => {
        if (cancelled) return;
        const map: Record<number, {
          dateOfBirth?: string | null; gender?: string | null;
          email?: string | null; phone?: string | null;
        }> = {};
        (request?.subjects ?? []).forEach((sub) => {
          if (sub.memberID != null) {
            map[sub.memberID] = {
              dateOfBirth: sub.dateOfBirth, gender: sub.gender, email: sub.email, phone: sub.phone,
            };
          }
        });
        // The requester is on the request's top-level fields rather than in subjects[].
        if (request?.matchedMemberID != null && !map[request.matchedMemberID]) {
          map[request.matchedMemberID] = {
            dateOfBirth: null, gender: request.gender, email: request.email, phone: request.phone,
          };
        }
        setSubmittedByMemberID(map);
      })
      .catch((e) => console.error('[CounseleesTab] could not load the request for merge details:', e));
    return () => { cancelled = true; };
  }, [active, requestID, requestService]);

  /**
   * The request's values for a counselee, but only when at least one would actually change something.
   *
   * ⚠️ Returning null hides the action. An icon that opens onto "nothing to apply" is worse than no
   * icon, and it would otherwise appear on every counselee of every approved request.
   */
  const submittedFor = useCallback((m: Member) => {
    const sub = submittedByMemberID[m.memberID];
    if (!sub) return null;
    const differs = (cur?: string | null, next?: string | null) => {
      const n = (next ?? '').trim();
      return n !== '' && n !== (cur ?? '').trim();
    };
    const worth = differs(m.dateOfBirth, sub.dateOfBirth)
      || differs(m.gender, sub.gender)
      || differs(m.email, sub.email)
      || differs(m.cellNumber, sub.phone);
    return worth ? sub : null;
  }, [submittedByMemberID]);

  const handleAdded = async () => {
    await fetchCounselees();
    onCaseChange?.();
  };

  const handleRemoveMember = async (member: Member) => {
    setRemovingId(member.memberID);
    try {
      await careCaseService.removeMember(caseID, member.memberID);
      await fetchCounselees();
      onCaseChange?.();
      toast.success('Removed', `${fullName(member)} removed from the case.`);
    } catch (e) {
      console.error('Error removing member:', e);
      toast.error('Error', 'Could not remove counselee. Please try again.');
    } finally {
      setRemovingId(null);
    }
  };

  const alreadyAdded = (m: Member) =>
    caseMemberIDs.includes(m.memberID);

  return (
    <Stack gap="lg">
      {/* ── Header: title + refresh + add button ── */}
      <Group justify="space-between" align="center">
        <Text fw={600} size="sm" c="dimmed">
          Counselees on this case ({counselees.length})
        </Text>
        <Group gap="xs">
          <Tooltip label="Refresh counselees" withArrow>
            <ActionIcon
              variant="subtle"
              onClick={fetchCounselees}
              loading={loadingCounselees}
              size="lg"
            >
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
          {/* Only when the case came from a request — see `requestID` on the props. */}
          {requestID != null && (
            <Button
              variant="light"
              leftSection={<IconFileImport size={16} />}
              onClick={() => setImportOpen(true)}
            >
              Import from request
            </Button>
          )}
          <Button
            leftSection={<IconUserPlus size={16} />}
            onClick={() => setDrawerOpen(true)}
          >
            Add Counselees
          </Button>
        </Group>
      </Group>

      {/* ── Counselees grid ── */}
      <Stack gap="xs">
        {loadingCounselees ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            <CounseleeCardSkeleton />
            <CounseleeCardSkeleton />
            <CounseleeCardSkeleton />
          </SimpleGrid>
        ) : counselees.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <ThemeIcon size={56} radius="xl" variant="light" color="teal">
                <IconUsers size={30} />
              </ThemeIcon>
              <Text fw={600}>No counselees yet</Text>
              <Text size="sm" c="dimmed" ta="center" maw={360}>
                Click "Add Counselees" above to search for a member or create a new one.
              </Text>
            </Stack>
          </Center>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {counselees.map((c) => (
              <CounseleeCard
                key={c.memberID}
                member={c}
                removing={removingId === c.memberID}
                onRemove={() => handleRemoveMember(c)}
                onOpenDetails={() => setDetailMember(c)}
                onFillFromRequest={submittedFor(c) ? () => setMergeMember(c) : undefined}
              />
            ))}
          </SimpleGrid>
        )}
      </Stack>

      <AddCounseleesDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        caseID={caseID}
        alreadyAdded={alreadyAdded}
        onAdded={handleAdded}
      />

      {requestID != null && (
        <ImportFromRequestModal
          caseID={caseID}
          requestID={requestID}
          existingMemberIDs={caseMemberIDs}
          opened={importOpen}
          onClose={() => setImportOpen(false)}
          onImported={handleAdded}
        />
      )}

      {mergeMember && submittedFor(mergeMember) && (
        <MergeFromSubmissionModal
          memberID={mergeMember.memberID}
          memberName={fullName(mergeMember)}
          current={{
            dateOfBirth: mergeMember.dateOfBirth,
            gender: mergeMember.gender,
            email: mergeMember.email,
            cellNumber: mergeMember.cellNumber,
          }}
          submitted={submittedFor(mergeMember)!}
          sourceLabel="request"
          opened={mergeMember !== null}
          onClose={() => setMergeMember(null)}
          onMerged={handleAdded}
        />
      )}

      {/* The card only has room for a name and one contact line; the rest lives here. */}
      <MemberDetailModal
        member={detailMember}
        opened={detailMember !== null}
        onClose={() => setDetailMember(null)}
        contextLabel="case"
      />
    </Stack>
  );
}